import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useUser } from '@/lib/auth';
import { useShowSaveToFolderModal } from '@/shared/features/quiz-folder';
import { useCanCreateClassroom } from '@/shared/hooks';
import { hasFlashcardCards } from '../../../flashcard/utils';
import { useCTAHandlers } from '../hooks/useCTAHandlers';
import { type CTAScenario } from '../hooks/useCTAScenario';
import { GuestCTAButtons } from './GuestCTAButtons';
import { OwnerCTAButtons } from './OwnerCTAButtons';
import { OwnerLockedCTAButtons } from './OwnerLockedCTAButtons';
import { ViewerCTAButtons } from './ViewerCTAButtons';
import { ViewerLockedCTAButtons } from './ViewerLockedCTAButtons';
export interface DesktopCTAButtonGroupProps {
  scenario: CTAScenario;
  quizId: string;
  quizData: QuizSetDetailsRes;
  className?: string;
}
export const DesktopCTAButtonGroup = ({
  scenario,
  quizId,
  quizData,
  className
}: DesktopCTAButtonGroupProps) => {
  const {
    user
  } = useUser();
  const canCreateClassroom = useCanCreateClassroom();
  const showSaveToFolderModal = useShowSaveToFolderModal();
  const {
    handleEnter,
    handleShare,
    handleDuplicate,
    handleEdit,
    handleGoToLms,
    handleDuplicateLocked,
    handleUpgradePlan,
    handleExportWorksheet,
    handleFlashcard,
    handleLinkToClassroom
  } = useCTAHandlers({
    quizId,
    quizData,
    scenario
  });
  const isZepAdmin = user?.admin && user.email.endsWith('@zep.us');
  const handleAvailableFlashcard = hasFlashcardCards(quizData.quizList) ? handleFlashcard : undefined;
  const content = (() => {
    switch (scenario) {
      case 'guest':
        return <GuestCTAButtons onCopy={handleDuplicate} onEnter={handleEnter} onExportWorksheet={handleExportWorksheet} onFlashcard={handleAvailableFlashcard} />;
      case 'owner':
        return <OwnerCTAButtons onEnter={handleEnter} onShare={handleShare} onGoToLms={handleGoToLms} onDuplicate={handleDuplicate} onEdit={handleEdit} onExportWorksheet={handleExportWorksheet} onFlashcard={handleAvailableFlashcard} onLinkToClassroom={canCreateClassroom ? handleLinkToClassroom : undefined}
        /*
          `currentFolderId` 를 빠뜨리면 `내 퀴즈`(폴더 밖) 행이 안 뜬다 — 이동은 빼낼
          폴더를 알아야 하는 요청이라서다. 상세 응답이 `folderId` 를 갖고 있으므로
          그대로 넘겨 **폴더 밖으로 꺼내는 길**을 연다. 폴더 밖 퀴즈면 `undefined` 라
          그 행이 숨는 것이 맞다(꺼낼 것이 없다).
        */ onMoveToFolder={() => void showSaveToFolderModal({
          id: quizId,
          intent: 'move',
          currentFolderId: quizData.folderId
        })} />;
      case 'viewer':
        return <ViewerCTAButtons onDuplicate={handleDuplicate} onEnter={handleEnter} onGoToLms={isZepAdmin ? handleGoToLms : undefined} onExportWorksheet={handleExportWorksheet} onFlashcard={handleAvailableFlashcard} />;
      case 'owner-locked':
        return <OwnerLockedCTAButtons onDuplicateLocked={handleDuplicateLocked} onUpgradePlan={handleUpgradePlan} onGoToLms={handleGoToLms} onEdit={handleEdit} onExportWorksheet={handleExportWorksheet} onFlashcard={handleAvailableFlashcard} />;
      case 'viewer-locked':
        return <ViewerLockedCTAButtons onCopy={handleDuplicate} onGoToLms={isZepAdmin ? handleGoToLms : undefined} onExportWorksheet={handleExportWorksheet} onFlashcard={handleAvailableFlashcard} />;
      default:
        // eslint-disable-next-line no-case-declarations
        const _exhaustiveCheck: never = scenario;
        return null;
    }
  })();
  return <div className={className} data-sentry-component="DesktopCTAButtonGroup" data-sentry-source-file="DesktopCTAButtonGroup.tsx">{content}</div>;
};